import { useCallback, useEffect, useRef, useState } from 'react';

/** Recording stops here whatever the speaker does. Sixty seconds of Opus is around 120KB. */
export const MAX_RECORDING_SECONDS: number = 60;

/**
 * What the browser is asked to record in, most wanted first.
 *
 * Chrome gives webm/opus and Safari gives mp4; the Operator names the file by this so the API
 * knows what it has been handed. An empty string is the last resort - `MediaRecorder` then picks
 * its own, and `mimeType` on the recorder reports what that was.
 */
const PREFERRED_TYPES: string[] = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    '',
];

export interface VoiceInput {
    /** Whether this browser can record at all. */
    supported: boolean;
    recording: boolean;
    /** Whole seconds recorded so far, for the counter next to the button. */
    seconds: number;
    /** Empty unless something went wrong that the user needs to know about. */
    error: string;
    start: () => void;
    stop: () => void;
}

/**
 * Record a short clip and hand it over as a data URL.
 *
 * The clip goes to the Operator over the chat socket that is already open, so there is no second
 * request to authorise here and nothing to configure. What this owns is the microphone: taking it,
 * showing that it is taken, and - the part that is easy to forget - giving it back, because a
 * `MediaStream` left running keeps the browser's recording indicator lit long after the panel is
 * closed.
 */
export function useVoiceInput(onClip: (audio: string, mime: string) => void): VoiceInput {
    const [recording, setRecording] = useState<boolean>(false);
    const [seconds, setSeconds] = useState<number>(0);
    const [error, setError] = useState<string>('');

    const recorder = useRef<MediaRecorder | null>(null);
    const chunks = useRef<Blob[]>([]);
    const ticker = useRef<ReturnType<typeof setInterval> | null>(null);

    const supported: boolean =
        typeof window !== 'undefined' &&
        typeof MediaRecorder !== 'undefined' &&
        navigator.mediaDevices !== undefined;

    /** Hand the microphone back and stop counting. Safe to call twice. */
    const release = useCallback((): void => {
        if (ticker.current !== null) {
            clearInterval(ticker.current);
            ticker.current = null;
        }
        const active: MediaRecorder | null = recorder.current;
        if (active !== null) {
            active.stream.getTracks().forEach((track: MediaStreamTrack): void => track.stop());
            recorder.current = null;
        }
        setRecording(false);
        setSeconds(0);
    }, []);

    // A panel closed mid-recording must not leave the microphone open.
    useEffect((): (() => void) => release, [release]);

    const stop = useCallback((): void => {
        const active: MediaRecorder | null = recorder.current;
        if (active !== null && active.state === 'recording') {
            // `onstop` sends the clip; the stream is released there so the last chunk still lands.
            active.stop();
        }
    }, []);

    const start = useCallback((): void => {
        if (!supported || recorder.current !== null) {
            return;
        }
        setError('');

        navigator.mediaDevices.getUserMedia({ audio: true })
            .then((stream: MediaStream): void => {
                const type: string = PREFERRED_TYPES.find(
                    (candidate: string): boolean =>
                        candidate === '' || MediaRecorder.isTypeSupported(candidate)
                ) ?? '';
                const active: MediaRecorder = new MediaRecorder(
                    stream, type === '' ? undefined : { mimeType: type }
                );

                chunks.current = [];
                active.ondataavailable = (event: BlobEvent): void => {
                    if (event.data.size > 0) {
                        chunks.current.push(event.data);
                    }
                };

                active.onstop = (): void => {
                    // Read the recorder's own mimeType rather than what was asked for: when the
                    // preferred type is refused it picks its own, and the Operator names the file
                    // it uploads from this.
                    const mime: string = active.mimeType || type || 'audio/webm';
                    const clip: Blob = new Blob(chunks.current, { type: mime });
                    chunks.current = [];
                    release();

                    if (clip.size === 0) {
                        return;
                    }
                    const reader: FileReader = new FileReader();
                    reader.onloadend = (): void => onClip(String(reader.result ?? ''), mime);
                    reader.readAsDataURL(clip);
                };

                recorder.current = active;
                active.start();
                setRecording(true);
                setSeconds(0);

                ticker.current = setInterval((): void => {
                    setSeconds((previous: number): number => {
                        const next: number = previous + 1;
                        if (next >= MAX_RECORDING_SECONDS) {
                            // Inside the tick rather than a second timer, so the cap and the
                            // counter can never disagree about how long it has been.
                            stop();
                        }
                        return next;
                    });
                }, 1000);
            })
            .catch((reason: Error): void => {
                // A refusal is the common case and is not a fault: the button stays, and pressing
                // it asks again. Everything else is reported as it comes.
                setError(reason.name === 'NotAllowedError'
                    ? 'Microphone access was refused.'
                    : 'The microphone could not be started.');
                release();
            });
    }, [onClip, release, stop, supported]);

    return { supported, recording, seconds, error, start, stop };
}
